import React, { useMemo } from "react"

import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { Minus, Plus, SortAsc, SortDesc } from "lucide-react"
import { DateTime, Duration } from "luxon"

import { Button } from "@/components/ui/button.tsx"
import { Skeleton } from "@/components/ui/skeleton.tsx"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table.tsx"
import { numberFmt } from "@/util/numberFmt.ts"

import { type Driver } from "../types/logisticsTypes.ts"
import { getTripHours } from "../util/getTripHours.ts"

const columnHelper = createColumnHelper<Driver>()

export default function DriverDetailTable({
  data,
  isLoading,
}: {
  data: Driver[]
  isLoading: boolean
}) {
  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "expand",
        header: () => <div className="py-4 px-4 w-[125px]">Show Trips</div>,
        cell: ({ row }) => (
          <div className="px-4">
            <Button
              variant="ghost"
              onClick={row.getToggleExpandedHandler()}
              disabled={!row.getCanExpand()}
            >
              {row.getIsExpanded() ? <Minus /> : <Plus />}
            </Button>
          </div>
        ),
      }),
      columnHelper.accessor((d) => d.name, {
        id: "name",
        header: () => <div className="py-4 px-4 w-[400px]">Name</div>,
        cell: (context) => <div className="py-4 px-4">{context.getValue()}</div>,
      }),
      columnHelper.accessor((d) => d.trips?.reduce((a, c) => a + (getTripHours(c) || 0), 0), {
        id: "hours",
        header: ({ column }) => (
          <div className="w-[150px]">
            <Button
              variant="ghost"
              onClick={column.getToggleSortingHandler()}
              className="w-full flex justify-center"
            >
              Hours
              <div>
                {column.getNextSortingOrder() == false && <SortAsc />}
                {column.getNextSortingOrder() == "asc" && <SortDesc />}
              </div>
            </Button>
          </div>
        ),
        cell: (context) => <div className="text-center">{numberFmt(context.getValue() || 0)}</div>,
      }),

      columnHelper.accessor(() => 0, {
        id: "target",
        header: ({ column }) => (
          <div>
            <Button
              variant="ghost"
              onClick={column.getToggleSortingHandler()}
              className="w-full flex justify-center"
            >
              Target Hours
              <div>
                {column.getNextSortingOrder() == false && <SortAsc />}
                {column.getNextSortingOrder() == "asc" && <SortDesc />}
              </div>
            </Button>
          </div>
        ),
        cell: (context) => (
          <div className="text-center">{numberFmt((context.getValue() || 0) / 60)}</div>
        ),
      }),
      columnHelper.accessor(
        (d) => {
          const hours = d.trips?.reduce((a, c) => a + (getTripHours(c) || 0), 0) || 0
          const target = 0

          if (hours == 0 || target == 0) return null
          return (1 - (hours - target) / target) * 100
        },
        {
          id: "efficiency",
          header: ({ column }) => (
            <div>
              <Button
                variant="ghost"
                onClick={column.getToggleSortingHandler()}
                className="w-full flex justify-center"
              >
                Efficiency
                <div>
                  {column.getNextSortingOrder() == false && <SortAsc />}
                  {column.getNextSortingOrder() == "asc" && <SortDesc />}
                </div>
              </Button>
            </div>
          ),
          cell: (context) => (
            <div
              className={`text-center font-semibold ${
                context.getValue() && Number(context.getValue()) >= 100
                  ? "text-status-good"
                  : "text-status-bad"
              }`}
            >
              {context.getValue() ? numberFmt(context.getValue() || 0) + "%" : "N/A"}
            </div>
          ),
        },
      ),
      columnHelper.accessor((d) => d.trips?.length, {
        id: "loads",
        header: ({ column }) => (
          <div className="w-[175px]">
            <Button
              variant="ghost"
              onClick={column.getToggleSortingHandler()}
              className="w-full flex justify-center"
            >
              Loads
              <div>
                {column.getNextSortingOrder() == false && <SortAsc />}
                {column.getNextSortingOrder() == "asc" && <SortDesc />}
              </div>
            </Button>
          </div>
        ),
        cell: (context) => <div className="text-center">{numberFmt(context.getValue() || 0)}</div>,
      }),
      columnHelper.accessor((d) => d.trips?.reduce((a, c) => a + (c.unloadVolume || 0), 0), {
        id: "unloadVolume",
        header: ({ column }) => (
          <div>
            <Button
              variant="ghost"
              onClick={column.getToggleSortingHandler()}
              className="w-full flex justify-start"
            >
              Manure
              <div>
                {column.getNextSortingOrder() == false && <SortAsc />}
                {column.getNextSortingOrder() == "asc" && <SortDesc />}
              </div>
            </Button>
          </div>
        ),
        cell: (context) => <div className="pl-6">{numberFmt(context.getValue() || 0)}</div>,
      }),
      columnHelper.accessor((d) => d.trips?.reduce((a, c) => a + (c.loadVolume || 0), 0), {
        id: "loadVolume",
        header: ({ column }) => (
          <div>
            <Button
              variant="ghost"
              onClick={column.getToggleSortingHandler()}
              className="w-full flex justify-start"
            >
              UF
              <div>
                {column.getNextSortingOrder() == false && <SortAsc />}
                {column.getNextSortingOrder() == "asc" && <SortDesc />}
              </div>
            </Button>
          </div>
        ),
        cell: (context) => <div className="pl-6">{numberFmt(context.getValue() || 0)}</div>,
      }),
    ],
    [],
  )

  const table = useReactTable<Driver>({
    data: data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowCanExpand: (row) =>
      row.original.trips != undefined ? row.original.trips.length > 0 : false,
  })

  return (
    <Table>
      {table.getHeaderGroups().map((hg) => (
        <TableHeader className="bg-accent sticky top-[70px] shadow-2xs z-100" key={hg.id}>
          <TableRow>
            {hg.headers.map((header, i) => (
              <TableHead key={i}>
                {flexRender(header.column.columnDef.header, header.getContext())}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
      ))}
      <TableBody>
        {isLoading || !data
          ? [...Array(31).keys()].map((i) => (
              <TableRow key={i}>
                <TableCell colSpan={table.getAllColumns().length} className="h-[52.5px]">
                  <Skeleton className="w-full h-full" />
                </TableCell>
              </TableRow>
            ))
          : table.getRowModel().rows.map((row, i) => (
              <React.Fragment key={i}>
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell, i) => (
                    <TableCell key={i}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
                {row.getIsExpanded() &&
                  row.original.trips?.map((t) => {
                    const efficiency = 0
                    return (
                      <TableRow key={t.id} className="bg-muted">
                        <TableCell></TableCell>
                        <TableCell className="px-4">
                          <div className="flex justify-between">
                            {t.farm || ""}
                            <div>
                              {t.start && DateTime.fromISO(t.start).toFormat("L/d h:mm a")}
                              <span className="px-2">-</span>
                              {t.end && DateTime.fromISO(t.end).toFormat("h:mm a ZZZZ")}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          {Duration.fromObject({
                            hours: getTripHours(t) || 0,
                          }).toFormat("h:mm")}
                        </TableCell>
                        <TableCell className="text-center">
                          {getTripHours(t) === null
                            ? "N/A"
                            : Duration.fromObject({
                                minutes: 0,
                              }).toFormat("h:mm")}
                        </TableCell>
                        <TableCell
                          className={`text-center ${
                            efficiency != undefined &&
                            (efficiency >= 100 ? "text-status-good" : "text-status-bad")
                          }`}
                        >
                          {efficiency == undefined ? "N/A" : numberFmt(efficiency) + "%"}
                        </TableCell>
                        <TableCell className="pl-14">{t.variant}</TableCell>

                        <TableCell className="pl-6">{numberFmt(t.unloadVolume || 0)}</TableCell>
                        <TableCell className="pl-6">{numberFmt(t.loadVolume || 0)}</TableCell>
                      </TableRow>
                    )
                  })}
              </React.Fragment>
            ))}
      </TableBody>
    </Table>
  )
}
